<!-- 배너 슬롯 관리 — 면(common/home/article/list)별로 한 화면씩 쓴다.
     라우터가 page prop 을 넘긴다: /ad/common /ad/home /ad/article /ad/list
     서버: /api/adSlot/* (관리자만)  ·  문서: docs/50-운영/13-배너-슬롯.md

     🔴 script 는 웹이 이스케이프 없이 그대로 렌더한다(= 임의 JS 실행).
        광고사에서 받은 원문만 붙여 넣는다. -->
<template>
  <div id="ad-slot-view">
    <div class="mb-4" style="display:flex;gap:10px">
      <vs-button :type="workspaceMode ? 'filled' : 'border'" @click="workspaceMode = true">새 배너관리 · 작업본 / 미리보기</vs-button>
      <vs-button :type="workspaceMode ? 'border' : 'filled'" @click="leaveWorkspace">기존 공개 배너 관리</vs-button>
    </div>
    <ad-workspace v-if="workspaceMode" ref="workspace" :page="page" />
    <div v-else>

    <vx-card :title="pageTitle" class="mb-4">
      <div class="ad-help">
        스크립트를 넣고 저장하면 <b>즉시 웹에 반영</b>된다(Redis <code>ad:slots</code>).
        비워 두면 그 자리는 아예 렌더되지 않는다.
        기기 구분은 CSS 로 한다 — PC 986px↑ / 태블릿 985~691 / 모바일 690↓.
        <span class="ad-help-warn">스크립트는 그대로 실행된다. 신뢰할 수 있는 원문만 붙여 넣을 것.</span>
      </div>
      <div class="mt-3">
        <vs-button color="primary" type="border" class="p-2 cursor-pointer" @click="fetch()">새로고침</vs-button>
        <vs-button color="dark" type="border" class="p-2 cursor-pointer ml-2" @click="sync()">웹에 다시 반영</vs-button>
        <span class="ad-count">슬롯 {{ list.length }}개 · 노출 중 {{ liveCount }}개</span>
      </div>
    </vx-card>

    <!-- ── 배치도 ─────────────────────────────────────────────
         실제 화면 골격을 흉내 낸 그림이다. 칸을 누르면 그 슬롯 편집창이 열린다. -->
    <vx-card title="배치도 (PC 기준)" class="mb-4">
      <div class="ad-map">
        <div class="ad-map-page">
          <div class="ad-map-bar">헤더 / 상단 메뉴</div>

          <div class="ad-map-cols" :class="{ 'one-col': !layout.side.length }">
            <div class="ad-map-main">
              <div v-for="(it, i) in layout.main" :key="'m' + i">
                <div v-if="it.slot" class="ad-map-slot" :class="slotClass(it.slot)"
                     @click="edit(bySlot(it.slot))" :title="tip(it.slot)">
                  <span class="ad-map-code">{{ it.slot }}</span>
                  <span class="ad-map-name">{{ nameOf(it.slot) }}</span>
                  <span class="ad-map-state">{{ stateLabel(it.slot) }}</span>
                </div>
                <div v-else class="ad-map-content">{{ it.label }}</div>
              </div>
            </div>

            <div class="ad-map-side" v-if="layout.side.length">
              <div v-for="(it, i) in layout.side" :key="'s' + i">
                <div v-if="it.slot" class="ad-map-slot side" :class="slotClass(it.slot)"
                     @click="edit(bySlot(it.slot))" :title="tip(it.slot)">
                  <span class="ad-map-code">{{ it.slot }}</span>
                  <span class="ad-map-name">{{ nameOf(it.slot) }}</span>
                  <span class="ad-map-state">{{ stateLabel(it.slot) }}</span>
                </div>
                <div v-else class="ad-map-content side">{{ it.label }}</div>
              </div>
            </div>
          </div>

          <div class="ad-map-bar">푸터</div>

          <div v-for="(it, i) in layout.float" :key="'f' + i" class="ad-map-float"
               @click="edit(bySlot(it.slot))">
            <span class="ad-map-code">{{ it.slot }}</span>
            <span class="ad-map-name">{{ nameOf(it.slot) }} — 모바일 화면 하단 고정</span>
            <span class="ad-map-state">{{ stateLabel(it.slot) }}</span>
          </div>
        </div>
        <div class="ad-map-legend">
          <span class="lg live"></span> 노출 중
          <span class="lg off"></span> 중지
          <span class="lg empty"></span> 스크립트 없음(렌더 안 됨)
        </div>
      </div>
    </vx-card>

    <!-- ── 목록 ───────────────────────────────────────────── -->
    <vs-table :data="list">
      <template slot="thead">
        <vs-th style="width: 120px">슬롯</vs-th>
        <vs-th>위치</vs-th>
        <vs-th style="width: 120px">기기</vs-th>
        <vs-th style="width: 110px">예약높이</vs-th>
        <vs-th style="width: 170px">게재기간</vs-th>
        <vs-th style="width: 90px">스크립트</vs-th>
        <vs-th style="width: 80px">노출</vs-th>
        <vs-th style="width: 80px"></vs-th>
      </template>

      <template slot-scope="{data}">
        <vs-tr :data="tr" :key="tr.slot" v-for="tr in data">
          <vs-td><code>{{ tr.slot }}</code></vs-td>
          <vs-td>
            {{ tr.name }}
            <div class="ad-memo" v-if="tr.memo">{{ tr.memo }}</div>
          </vs-td>
          <vs-td>{{ deviceLabel(tr.device) }}</vs-td>
          <vs-td>{{ tr.height_pc || 0 }} / {{ tr.height_mobile || 0 }}</vs-td>
          <vs-td>{{ periodLabel(tr) }}</vs-td>
          <vs-td>
            <span v-if="hasScript(tr)" class="ad-badge on">있음</span>
            <span v-else class="ad-badge off">없음</span>
          </vs-td>
          <vs-td>
            <vs-switch :value="!!Number(tr.state)" @input="toggle(tr, $event)" />
          </vs-td>
          <vs-td>
            <vs-button color="primary" type="border" size="small" class="cursor-pointer"
                       @click="edit(tr)">편집</vs-button>
          </vs-td>
        </vs-tr>
      </template>
    </vs-table>

    <!-- ── 편집 ───────────────────────────────────────────── -->
    <vs-popup :title="form.slot ? (form.slot + ' — ' + form.name) : ''" :active.sync="popup">
      <div v-if="form.slot" class="ad-form">

        <label>광고 스크립트</label>
        <textarea v-model="form.script" rows="10" class="ad-script"
                  placeholder="광고사에서 받은 태그를 그대로 붙여 넣습니다. 비우면 렌더되지 않습니다."></textarea>
        <div class="ad-note">
          &lt;script&gt; · &lt;ins&gt; · &lt;iframe&gt; 모두 그대로 나갑니다.
          이미지 배너라면 &lt;a href="..."&gt;&lt;img src="..."&gt;&lt;/a&gt; 로 넣으면 됩니다.
        </div>

        <label class="mt-4">노출 기기</label>
        <div class="ad-devs">
          <vs-checkbox v-model="dev.pc">PC (986px↑)</vs-checkbox>
          <vs-checkbox v-model="dev.tablet">태블릿 (985~691)</vs-checkbox>
          <vs-checkbox v-model="dev.mobile">모바일 (690↓)</vs-checkbox>
        </div>

        <div class="ad-row mt-4">
          <div>
            <label>PC 예약높이 (px)</label>
            <vs-input type="number" v-model="form.heightPc" />
          </div>
          <div>
            <label>모바일 예약높이 (px)</label>
            <vs-input type="number" v-model="form.heightMobile" />
          </div>
        </div>
        <div class="ad-note">
          광고는 늦게 뜬다. 미리 자리를 잡아 두지 않으면 본문이 아래로 튄다(CLS).
          0 이면 자리를 잡지 않는다.
        </div>

        <div class="ad-row mt-4">
          <div>
            <label>게재 시작 (비우면 즉시)</label>
            <vs-input type="datetime-local" v-model="form.startAt" />
          </div>
          <div>
            <label>게재 종료 (비우면 무기한)</label>
            <vs-input type="datetime-local" v-model="form.endAt" />
          </div>
        </div>

        <div class="mt-4">
          <vs-checkbox v-model="form.stateOn">노출</vs-checkbox>
        </div>

        <label class="mt-4">메모 (광고주·단가 등)</label>
        <vs-input v-model="form.memo" class="w-full" />

        <div class="ad-actions">
          <vs-button color="primary" class="p-3 cursor-pointer" @click="save()">저장</vs-button>
          <vs-button color="dark" type="border" class="p-3 cursor-pointer ml-3"
                     @click="popup = false">취소</vs-button>
          <vs-button color="danger" type="border" class="p-3 cursor-pointer ml-3"
                     @click="clearScript()">스크립트 비우기</vs-button>
        </div>
      </div>
    </vs-popup>
    </div>
  </div>
</template>

<script>
import AdWorkspace from './AdWorkspace';
// 면별 배치도. slot 이 있으면 배너 칸, label 만 있으면 기사/목록 같은 콘텐츠 자리다.
// 웹 템플릿(joongang_www/news/view/joongang/*.ejs)에 실제로 박아 둔 순서와 맞춘다.
const LAYOUT = {
  common: {
    title: '공통 (전 페이지)',
    main: [
      { slot: 'top' },
      { label: '페이지 본문 (홈·기사·목록 공통)' },
      { slot: 'footer' },
    ],
    side: [], float: [],
  },
  home: {
    title: '메인홈',
    main: [
      { label: '메인 헤드라인' },
      { slot: 'home_lead' },
      { slot: 'home_m1' },
      { label: '중탑1' },
      { slot: 'home_mid1' },
      { label: '중탑2' },
      { slot: 'home_mid2' },
      { label: '중탑3' },
      { slot: 'home_mid3' },
      { slot: 'home_bottom' },
      { label: '섹션별 뉴스' },
    ],
    side: [
      { slot: 'home_side1' },
      { label: '많이 본 기사' },
      { slot: 'home_side2' },
      { label: '오피니언 / 중앙신문TV' },
      { slot: 'home_side3' },
    ],
    float: [],
  },
  article: {
    title: '기사면',
    main: [
      { label: '제목 · 부제 · 기자' },
      { slot: 'art_head' },
      { label: '본문 1번째 문단' },
      { slot: 'art_p1' },
      { label: '본문 2번째 문단' },
      { slot: 'art_p2' },
      { label: '본문 3번째 문단' },
      { slot: 'art_p3' },
      { label: '본문 4번째 문단' },
      { slot: 'art_p4' },
      { label: '본문 5번째 문단' },
      { slot: 'art_p5' },
      { label: '본문 6번째 문단' },
      { slot: 'art_p6' },
      { label: '본문 7번째 문단' },
      { slot: 'art_p7' },
      { label: '본문 8번째 문단' },
      { slot: 'art_p8' },
      { label: '본문 9번째 문단' },
      { slot: 'art_p9' },
      { label: '본문 10번째 문단' },
      { slot: 'art_p10' },
      { label: '(이후 문단들)' },
      { slot: 'art_end' },
      { label: '태그' },
      { slot: 'art_tag' },
      { label: '관련기사' },
      { slot: 'art_related' },
      { label: '댓글' },
      { slot: 'art_comment' },
    ],
    side: [
      { slot: 'art_side1' },
      { label: '많이 본 기사' },
      { slot: 'art_side2' },
      { label: '같은 분야 기사' },
      { slot: 'art_side3' },
    ],
    float: [{ slot: 'art_m_sticky' }],
  },
  list: {
    title: '목록면 (카테고리·검색·뉴스리스트)',
    main: [
      { slot: 'list_top' },
      { label: '기사 목록 (앞부분)' },
      { slot: 'list_mid' },
      { label: '기사 목록 (뒷부분)' },
      { slot: 'list_bottom' },
      { label: '페이지 이동' },
    ],
    side: [
      { slot: 'list_side1' },
      { label: '많이 본 기사' },
      { slot: 'list_side2' },
    ],
    float: [],
  },
};

export default {
  components: { AdWorkspace },
  beforeRouteLeave(to, from, next) {
    if (to.path === '/pages/login' && ['expired', 'logout'].includes(to.query.session)) return next();
    const editor = this.$refs.workspace;
    next(!editor || editor.canLeave());
  },
  beforeRouteUpdate(to, from, next) {
    const editor = this.$refs.workspace;
    next(!editor || editor.canLeave());
  },
  props: {
    page: { type: String, required: true },
  },
  data() {
    return {
      workspaceMode: true,
      list: [],
      popup: false,
      form: {},
      dev: { pc: true, tablet: true, mobile: true },
    };
  },
  computed: {
    layout() { return LAYOUT[this.page] || { title: this.page, main: [], side: [], float: [] }; },
    pageTitle() { return '배너 — ' + this.layout.title; },
    liveCount() {
      const self = this;
      return this.list.filter(function (r) { return Number(r.state) && self.hasScript(r); }).length;
    },
  },
  watch: {
    // 같은 컴포넌트를 면만 바꿔 재사용하므로 라우트가 바뀌면 다시 읽는다.
    page() { this.fetch(); },
  },
  created() { this.fetch(); },
  methods: {
    leaveWorkspace() {
      const editor = this.$refs.workspace;
      if (!editor || editor.canLeave()) this.workspaceMode = false;
    },
    hasScript(r) { return !!(r && r.script && String(r.script).trim()); },
    bySlot(slot) { return this.list.find(function (r) { return r.slot === slot; }) || null; },
    nameOf(slot) { const r = this.bySlot(slot); return r ? r.name : slot; },
    slotClass(slot) {
      const r = this.bySlot(slot);
      if (!r || !this.hasScript(r)) return 'empty';
      return Number(r.state) ? 'live' : 'off';
    },
    stateLabel(slot) {
      const r = this.bySlot(slot);
      if (!r) return '';
      if (!this.hasScript(r)) return '비어 있음';
      return Number(r.state) ? '노출' : '중지';
    },
    tip(slot) {
      const r = this.bySlot(slot);
      if (!r) return slot;
      return r.name + ' · ' + this.deviceLabel(r.device) +
             ' · 예약높이 ' + (r.height_pc || 0) + '/' + (r.height_mobile || 0) + 'px';
    },
    deviceLabel(d) {
      const s = String(d || 'all');
      if (s === 'all') return '전체';
      const map = { pc: 'PC', tablet: '태블릿', mobile: '모바일' };
      return s.split(',').map(function (x) { return map[x.trim()] || x; }).join('·');
    },
    // 🔴 DATETIME 은 mysql 드라이버가 Date 로 주고 JSON 이 **UTC ISO** 로 직렬화한다
    //    ('2026-09-20 09:00' → '2026-09-20T00:00:00.000Z'). 문자열을 그냥 자르면
    //    9시간 어긋난 값이 화면에 찍힌다 — toLocalInput 과 같은 변환을 거쳐야 한다.
    periodLabel(r) {
      const self = this;
      const f = function (v) { return v ? self.toLocalInput(v).replace('T', ' ') : ''; };
      if (!r.start_at && !r.end_at) return '상시';
      return (f(r.start_at) || '즉시') + ' ~ ' + (f(r.end_at) || '무기한');
    },
    fetch() {
      const thisIns = this;
      this.$vs.loading({ scale: 1.2 });
      this.$http.post('/api/adSlot/list', { page: this.page }).then(function (res) {
        thisIns.$vs.loading.close();
        thisIns.list = res.data.list || [];
      }).catch(function () {
        thisIns.$vs.loading.close();
        thisIns.$vs.notify({ title: '오류', text: '배너 목록을 불러오지 못했습니다.', color: 'danger' });
      });
    },
    // DATETIME 은 서버가 ISO 문자열로 주기도 한다. input 은 'YYYY-MM-DDTHH:mm' 만 받는다.
    toLocalInput(v) {
      if (!v) return '';
      const d = new Date(v);
      if (isNaN(d.getTime())) return String(v).replace(' ', 'T').substring(0, 16);
      const p = function (n) { return (n < 10 ? '0' : '') + n; };
      return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate()) +
             'T' + p(d.getHours()) + ':' + p(d.getMinutes());
    },
    edit(row) {
      if (!row) return;
      this.form = {
        slot: row.slot,
        name: row.name,
        script: row.script || '',
        heightPc: Number(row.height_pc) || 0,
        heightMobile: Number(row.height_mobile) || 0,
        startAt: this.toLocalInput(row.start_at),
        endAt: this.toLocalInput(row.end_at),
        memo: row.memo || '',
        stateOn: !!Number(row.state),
      };
      const d = String(row.device || 'all');
      const all = d === 'all';
      this.dev = {
        pc: all || d.indexOf('pc') >= 0,
        tablet: all || d.indexOf('tablet') >= 0,
        mobile: all || d.indexOf('mobile') >= 0,
      };
      this.popup = true;
    },
    clearScript() {
      if (!confirm('이 슬롯의 스크립트를 비웁니다. 웹에서 즉시 사라집니다.')) return;
      this.form.script = '';
      this.save();
    },
    deviceValue() {
      const on = [];
      if (this.dev.pc) on.push('pc');
      if (this.dev.tablet) on.push('tablet');
      if (this.dev.mobile) on.push('mobile');
      if (on.length === 3) return 'all';
      return on.join(',');
    },
    save() {
      const thisIns = this;
      const device = this.deviceValue();
      if (!device) {
        this.$vs.notify({ title: '확인', text: '노출 기기를 하나 이상 선택해야 합니다.', color: 'warning' });
        return;
      }
      this.$vs.loading({ scale: 1.2 });
      this.$http.post('/api/adSlot/save', {
        slot: this.form.slot,
        script: this.form.script,
        device: device,
        state: this.form.stateOn ? 1 : 0,
        startAt: this.form.startAt ? this.form.startAt.replace('T', ' ') + ':00' : null,
        endAt: this.form.endAt ? this.form.endAt.replace('T', ' ') + ':00' : null,
        heightPc: Number(this.form.heightPc) || 0,
        heightMobile: Number(this.form.heightMobile) || 0,
        memo: this.form.memo,
      }).then(function (res) {
        thisIns.$vs.loading.close();
        thisIns.popup = false;
        // DB 는 저장됐지만 Redis 반영이 실패하면 웹이 최대 60초 더 옛 값을 보여 준다.
        if (res.data && res.data.redis === false) {
          thisIns.$vs.notify({
            title: '저장(주의)', color: 'warning',
            text: '저장은 됐지만 Redis 반영에 실패했습니다. [웹에 다시 반영] 을 눌러 주세요.',
          });
        } else {
          thisIns.$vs.notify({ title: '저장', text: '웹에 반영했습니다.', color: 'success' });
        }
        thisIns.fetch();
      }).catch(function (e) {
        thisIns.$vs.loading.close();
        const msg = (e.response && e.response.data && e.response.data.msg) || '저장에 실패했습니다.';
        thisIns.$vs.notify({ title: '오류', text: msg, color: 'danger' });
      });
    },
    // 목록에서 바로 켜고 끈다(사고 대응).
    toggle(row, on) {
      const thisIns = this;
      this.$http.post('/api/adSlot/toggle', { slot: row.slot, state: on ? 1 : 0 })
        .then(function (res) {
          row.state = on ? 1 : 0;
          if (res.data && res.data.redis === false) {
            thisIns.$vs.notify({
              title: '변경(주의)', color: 'warning',
              text: 'Redis 반영에 실패했습니다. [웹에 다시 반영] 을 눌러 주세요.',
            });
          } else {
            thisIns.$vs.notify({ title: '변경', text: row.slot + ' ' + (on ? '노출' : '중지'), color: 'success' });
          }
        }).catch(function () {
          thisIns.$vs.notify({ title: '오류', text: '상태를 바꾸지 못했습니다.', color: 'danger' });
          thisIns.fetch();
        });
    },
    sync() {
      const thisIns = this;
      this.$http.post('/api/adSlot/sync', {}).then(function () {
        thisIns.$vs.notify({ title: '반영', text: 'Redis 에 다시 썼습니다.', color: 'success' });
      }).catch(function () {
        thisIns.$vs.notify({ title: '오류', text: 'Redis 반영에 실패했습니다.', color: 'danger' });
      });
    },
  },
};
</script>

<style>
#ad-slot-view .ad-help { font-size: 13px; color: #555; line-height: 1.7; }
#ad-slot-view .ad-help-warn { color: #c0392b; display: block; margin-top: 4px; }
#ad-slot-view .ad-count { margin-left: 12px; font-size: 12px; color: #888; }
#ad-slot-view .ad-memo { font-size: 11px; color: #999; }
#ad-slot-view .ad-badge { padding: 1px 7px; border-radius: 3px; font-size: 11px; color: #fff; }
#ad-slot-view .ad-badge.on { background: #28c76f; }
#ad-slot-view .ad-badge.off { background: #c8c8c8; }

/* 배치도 */
#ad-slot-view .ad-map-page { border: 1px solid #ddd; border-radius: 6px; padding: 10px; background: #fafafa; }
#ad-slot-view .ad-map-bar {
  background: #e8eaf0; color: #666; font-size: 12px; text-align: center;
  padding: 8px; border-radius: 4px; margin-bottom: 8px;
}
#ad-slot-view .ad-map-cols { display: flex; gap: 10px; align-items: flex-start; }
#ad-slot-view .ad-map-cols.one-col .ad-map-main { flex: 1 1 100%; }
#ad-slot-view .ad-map-main { flex: 1 1 70%; min-width: 0; }
#ad-slot-view .ad-map-side { flex: 0 0 200px; }
#ad-slot-view .ad-map-content {
  background: #fff; border: 1px dashed #d5d5d5; color: #aaa; font-size: 12px;
  padding: 10px; border-radius: 4px; margin-bottom: 6px; text-align: center;
}
#ad-slot-view .ad-map-slot {
  display: flex; align-items: center; gap: 8px; cursor: pointer;
  padding: 7px 10px; border-radius: 4px; margin-bottom: 6px;
  border: 1px solid transparent; font-size: 12px;
}
#ad-slot-view .ad-map-slot.side { display: block; text-align: center; }
#ad-slot-view .ad-map-slot:hover { outline: 2px solid #7367f0; }
#ad-slot-view .ad-map-slot.live { background: #dff7e9; border-color: #28c76f; color: #1b7a45; }
#ad-slot-view .ad-map-slot.off { background: #fdeaea; border-color: #ea5455; color: #9d2b2c; }
#ad-slot-view .ad-map-slot.empty { background: #f1f1f1; border-color: #ccc; color: #999; }
#ad-slot-view .ad-map-code { font-family: monospace; font-weight: 700; }
#ad-slot-view .ad-map-name { flex: 1 1 auto; }
#ad-slot-view .ad-map-state { font-size: 11px; opacity: .8; }
#ad-slot-view .ad-map-float {
  margin-top: 8px; padding: 7px 10px; border-radius: 4px; cursor: pointer;
  border: 1px dashed #7367f0; background: #f3f2ff; font-size: 12px;
  display: flex; align-items: center; gap: 8px;
}
#ad-slot-view .ad-map-legend { margin-top: 10px; font-size: 12px; color: #777; }
#ad-slot-view .ad-map-legend .lg {
  display: inline-block; width: 11px; height: 11px; border-radius: 2px;
  margin: 0 4px 0 14px; vertical-align: middle;
}
#ad-slot-view .ad-map-legend .lg.live { background: #28c76f; }
#ad-slot-view .ad-map-legend .lg.off { background: #ea5455; }
#ad-slot-view .ad-map-legend .lg.empty { background: #ccc; }

/* 편집 팝업은 body 직속으로 붙어 #ad-slot-view 밖이다 */
.con-vs-popup .ad-form label {
  display: block; font-size: 12px; font-weight: 600; color: #555; margin-bottom: 4px;
}
.con-vs-popup .ad-script {
  width: 100%; font-family: Consolas, monospace; font-size: 12px; line-height: 1.5;
  padding: 8px; border: 1px solid #d8d6de; border-radius: 5px; resize: vertical;
}
.con-vs-popup .ad-note { font-size: 11px; color: #999; margin-top: 4px; line-height: 1.6; }
.con-vs-popup .ad-devs { display: flex; gap: 18px; }
.con-vs-popup .ad-row { display: flex; gap: 16px; }
.con-vs-popup .ad-row > div { flex: 1 1 50%; }
.con-vs-popup .ad-actions { margin-top: 24px; text-align: center; }
</style>
